import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../../hooks/axios';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import Input from '../../components/form/input/InputField';
import AdminPageShell from '../../components/admin/AdminPageShell';
import AdminCard from '../../components/admin/AdminCard';
import AdminListToolbar from '../../components/admin/AdminListToolbar';
import {
  adminAlertErrorClass,
  adminEditLinkClass,
  adminTableClass,
  adminTableEmptyClass,
  adminTableHeadClass,
  adminTableTdClass,
  adminTableThClass,
  adminTableWrapperClass,
} from '../../components/admin/adminStyles';
import {
  SEO_PLACE_TYPE_OPTIONS,
  placeTypeLabel,
  type SeoPlaceRecord,
} from './seoPlaceTypes';

export default function SeoPlaces() {
  const [items, setItems] = useState<SeoPlaceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [placeType, setPlaceType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchItems = async () => {
    setLoading(true);
    setError('');
    try {
      const collected: SeoPlaceRecord[] = [];
      let cursor: string | null = null;
      for (let page = 0; page < 20; page += 1) {
        const params = new URLSearchParams({ limit: '500' });
        if (placeType) params.set('placeType', placeType);
        if (searchTerm.trim()) params.set('search', searchTerm.trim());
        if (cursor) params.set('cursor', cursor);
        const response = await axiosInstance.get(`/seo-places?${params}`);
        const data = response.data as {
          items: SeoPlaceRecord[];
          nextCursor: string | null;
        };
        collected.push(...(data.items ?? []));
        cursor = data.nextCursor;
        if (!cursor) break;
      }
      setItems(collected);
    } catch {
      setError('Failed to fetch SEO places');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchItems, 250);
    return () => clearTimeout(t);
  }, [placeType, searchTerm]);

  return (
    <AdminPageShell>
      <PageBreadcrumb
        pageTitle="SEO Places"
        description="Edit shared airport, location, and cruise port SEO copy used across all brand sites."
      />

      <AdminCard>
        <AdminListToolbar>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <select
              value={placeType}
              onChange={(e) => setPlaceType(e.target.value)}
              className="h-11 rounded-lg border border-gray-200 bg-white px-4 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            >
              {SEO_PLACE_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <Input
              type="text"
              placeholder="Search slug, name, or IATA…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </AdminListToolbar>

        {error && <div className={`mb-4 ${adminAlertErrorClass}`}>{error}</div>}

        <div className={adminTableWrapperClass}>
          <table className={adminTableClass}>
            <thead className={adminTableHeadClass}>
              <tr>
                <th className={adminTableThClass}>Type</th>
                <th className={adminTableThClass}>Slug</th>
                <th className={adminTableThClass}>Name</th>
                <th className={adminTableThClass}>Meta title</th>
                <th className={adminTableThClass}>Published</th>
                <th className={adminTableThClass}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className={adminTableEmptyClass}>
                    Loading…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className={adminTableEmptyClass}>
                    No places found
                  </td>
                </tr>
              ) : (
                items.map((row) => (
                  <tr key={row.id}>
                    <td className={adminTableTdClass}>
                      {placeTypeLabel(row.placeType)}
                    </td>
                    <td className={adminTableTdClass}>{row.slug}</td>
                    <td className={adminTableTdClass}>
                      <div className="font-medium">{row.name}</div>
                      {row.iata ? (
                        <div className="text-xs text-gray-500">{row.iata}</div>
                      ) : null}
                    </td>
                    <td className={adminTableTdClass}>{row.seo.metaTitle}</td>
                    <td className={adminTableTdClass}>
                      {row.published ? 'Yes' : 'No'}
                    </td>
                    <td className={adminTableTdClass}>
                      <Link to={`/seo-places/edit/${row.id}`} className={adminEditLinkClass}>
                        Edit
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </AdminCard>
    </AdminPageShell>
  );
}
